import { useStandardProOrgConditionChecker } from 'conditions/hooks/useStandardProOrgConditionChecker'
import { FilterStruct } from 'conditions/types'
import { UserActionsContextType } from 'contexts/userActions/UserActionsContext'
import { useUserActionsContext } from 'contexts/userActions/useUserActionsContext'
import { useStructs } from 'contexts/structs/hooks/useStructs'
import { Struct, StructsLoadInfo } from 'contexts/structs/types/types'
import { TranslatableText } from 'persistentContent/promoDialog/usePromoDialogStructs'
import { orgSelectors } from 'ducks/orgs'
import { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { OrgFeatureSettings } from 'types/orgs'

// Feature flag structs types and functions
export type FeatureFlagOptState =
  | 'enabled_user_optout'
  | 'disabled_user_optin'
  | 'enabled_org_optout'
  | 'disabled_org_optin'
  | 'disabled_user_apply'
  | 'disabled_org_apply'
export type FeatureFlagState = 'enabled' | 'disabled' | FeatureFlagOptState

export type FeatureFlagType = {
  description: TranslatableText
  default_state: FeatureFlagState
  apply_emails_or_url?: string
  cases?: {
    description: string
    state: FeatureFlagState
    conditions: FilterStruct[]
  }[]
  more_info_url: TranslatableText
}

export const useFeatureFlagStructs = () => {
  return useStructs<FeatureFlagType>('feature-flag')
}

// Type for feature flag names (replaces SPLIT_NAMES)
// Feature flags are dynamically loaded from structs, so we use a branded string type
export type FeatureFlagName = string & { readonly __brand: unique symbol }

// Prefix for localStorage keys used to override feature flags (used in tests and dev tools).
// Duplicated (do not import app code into runners): spa/e2e/utils/featureFlag.ts;
// see .cursor/skills/bdd-tdd-workflow/test-patterns.md.
export const FEATURE_FLAG_OVERRIDE_PREFIX = 'OVERRIDE_FF_'

export const featureFlagValueFromLocalStorage = (featureFlagName: FeatureFlagName | string) => {
  return window.localStorage.getItem(FEATURE_FLAG_OVERRIDE_PREFIX + featureFlagName)
}

const createFeatureChecker = (
  structs: any[] | undefined,
  checker: (conditions: FilterStruct[]) => boolean,
  userActions: UserActionsContextType,
  orgFeatureSettings: Record<string, OrgFeatureSettings>
) => {
  return (featureFlagName: FeatureFlagName | string, target_value: string = 'on'): boolean | undefined => {
    const valueInLocalStorage = featureFlagValueFromLocalStorage(featureFlagName)
    if (valueInLocalStorage !== null) {
      return valueInLocalStorage === target_value
    }

    // Check if this is defined in the internal system (Structs Configs)
    // checker and userActions are always available (hard requirements after Split.io removal)
    const { isEnabled } = checkFeatureFlagStateFromStructs(
      structs || [],
      featureFlagName,
      checker,
      userActions.checkAction,
      orgFeatureSettings
    )
    return isEnabled
  }
}

export const useFeatureFlag = (featureFlagName: FeatureFlagName | string, target_value: string): boolean | undefined => {
  const userActions = useUserActionsContext()
  const orgFeatureSettings = useSelector(orgSelectors.getOrgFeatureSettings)

  const { structs } = useFeatureFlagStructs()
  const featureCheckerRef = useRef<FeatureChecker | undefined>(undefined)
  const featureCheckerWrapper = (ff: string, target: string = 'on') => {
    return featureCheckerRef.current?.(ff, target)
  }
  const checker = useStandardProOrgConditionChecker(featureCheckerWrapper)

  const featureChecker = useMemo(() => {
    const checkerFn = createFeatureChecker(
      structs,
      checker,
      userActions,
      orgFeatureSettings
    )
    featureCheckerRef.current = checkerFn
    return checkerFn
  }, [structs, checker, userActions, orgFeatureSettings])

  return useMemo(() => {
    return featureChecker(featureFlagName, target_value)
  }, [featureChecker, featureFlagName, target_value])
}

export type FeatureChecker = (featureFlagName: FeatureFlagName | string, target_value?: string) => boolean | undefined

// For immediate resolution of a dynamic feature flag name (acts as a utility for studio)
export const useFeatureFlagDynamic = (): FeatureChecker => {
  const orgFeatureSettings = useSelector(orgSelectors.getOrgFeatureSettings)

  // New way to get feature flags
  const { structs } = useFeatureFlagStructs()
  const userActions = useUserActionsContext()
  const featureCheckerRef = useRef<FeatureChecker | undefined>(undefined)
  const featureCheckerWrapper = (ff: string, target: string = 'on') => {
    return featureCheckerRef.current?.(ff, target)
  }
  const checker = useStandardProOrgConditionChecker(featureCheckerWrapper)

  const featureChecker = useMemo(() => {
    const checkerFn = createFeatureChecker(
      structs,
      checker,
      userActions,
      orgFeatureSettings
    )
    featureCheckerRef.current = checkerFn
    return checkerFn
  }, [structs, checker, userActions, orgFeatureSettings])

  return featureChecker
}

export const getFeatureFlagState = (data: FeatureFlagType, checker: (c: FilterStruct[]) => boolean) => {
  if (data.cases) {
    for (const c of data.cases) {
      if (checker(c.conditions)) {
        return c.state
      }
    }
  }
  return data.default_state
}

export const getFeatureFlagUserActionKey = (state: FeatureFlagOptState, struct_key: string) => {
  return state === 'enabled_user_optout' ? 'feature_flag_opt_out_' + struct_key : 'feature_flag_opt_in_' + struct_key
}

export const checkFeatureFlagStateFromStructs = (
  structs: Struct<FeatureFlagType>[],
  featureName: FeatureFlagName | string,
  checker: (c: FilterStruct[]) => boolean,
  checkAction: (action_name: string) => boolean,
  orgFeatureSettings: Record<string, OrgFeatureSettings>
): { struct: Struct<FeatureFlagType> | undefined; isEnabled: boolean | undefined } => {
  const struct = structs.find((s) => s.key === featureName)
  let isEnabled: boolean | undefined
  if (struct) {
    let state = getFeatureFlagState(struct.data, checker)
    if (state === 'enabled') isEnabled = true
    else if (state === 'disabled') isEnabled = false
    else if (state === 'enabled_user_optout') {
      const actionName = getFeatureFlagUserActionKey(state, struct.key)
      isEnabled = !checkAction(actionName)
    } else if (state === 'disabled_user_optin') {
      const actionName = getFeatureFlagUserActionKey(state, struct.key)
      isEnabled = checkAction(actionName)
    } else if (state === 'enabled_org_optout') {
      isEnabled = !orgFeatureSettings[struct.key]?.optout
    } else if (state === 'disabled_org_optin') {
      isEnabled = !!orgFeatureSettings[struct.key]?.optin
    }
  }
  return { struct, isEnabled }
}

export type FeatureFlagStateMap = Partial<Record<FeatureFlagState, Struct<FeatureFlagType>[]>>

export function useFeatureFlagStructsWithState(): StructsLoadInfo<FeatureFlagType> & {
  states: FeatureFlagStateMap
} {
  const { loadState, structs, version } = useFeatureFlagStructs()
  const featureChecker = useFeatureFlagDynamic()
  const checker = useStandardProOrgConditionChecker(featureChecker)
  const states = useMemo(() => {
    let states: FeatureFlagStateMap = {}
    for (const struct of structs) {
      let state = getFeatureFlagState(struct.data, checker)
      let list = states[state]
      if (list) list.push(struct)
      else states[state] = [struct]
    }
    return states
  }, [structs, checker])

  return useMemo(() => ({ loadState, structs, states, version }), [loadState, structs, states, version])
}
